<template>
  <main class="account" :class="{ 'account--hide-image': hideImage }">
    <section class="account__column account__column--cta">
      <div class="heading-container">
        <h1 data-cy="login-title">{{ $t('pages.login.headLogin') }}</h1>
        <p>{{ $t('pages.login.textIntroLogin') }}</p>
      </div>

      <BaseAlert
        :value="isLocked"
        :title="$t('pages.login.alertLockedTitle')"
        variant="error"
        multi-line
        data-cy="alert-account-locked"
      >
        <i18next path="pages.login.alertLocked" tag="p">
          <strong v-if="lockedDuration && !permanentLockout">{{
            $t('pages.login.alertLockedTime', {
              duration: time.duration,
              unit: time.unit
            })
          }}</strong>
        </i18next>
        <template #actions>
          <BaseButton to="/request-password" variant="tertiary" size="sm">
            {{ $t('pages.login.buttonForgotPassword') }}
          </BaseButton>
        </template>
      </BaseAlert>

      <form
        v-if="!isLocked"
        data-cy="login-form"
        @submit.prevent="authenticate(email, password)"
      >
        <div class="login-form u-margin-bottom-4x">
          <div class="form-group">
            <label for="login-email">{{ $t('pages.login.labelEmail') }}</label>
            <input
              id="login-email"
              v-model.trim="email"
              class="form-control"
              data-cy="login-email"
              type="text"
              :placeholder="$t('pages.login.placeholderEmail')"
              :disabled="isLoading"
              autofocus
            />
          </div>

          <div class="form-group">
            <label for="login-pw">{{ $t('pages.login.labelPassword') }}</label>
            <input
              id="login-pw"
              v-model="password"
              class="form-control"
              data-cy="login-password"
              type="password"
              :placeholder="$t('pages.login.placeholderPassword')"
              :disabled="isLoading"
            />
          </div>
        </div>

        <BaseAlert
          :value="showDisabledAccountAlert"
          class="u-margin-bottom-4x"
          variant="error"
          data-cy="alert-account-disabled"
          multi-line
          :title="$t('pages.login.alertAccountNotSupportedTitle')"
        >
          {{ $t('pages.login.alertAccountNotSupported') }}
        </BaseAlert>

        <BaseAlert
          :value="showAccountMigratedAlert"
          class="u-margin-bottom-4x"
          variant="error"
          data-cy="alert-account-migrated"
          multi-line
          :title="$t('pages.login.alertAccountMigratedTitle')"
        >
          <span v-html="$t('pages.login.alertAccountMigrated')"></span>
        </BaseAlert>

        <BaseAlert
          v-model="showLoginFailedAlert"
          variant="error"
          data-cy="alert-login-failed"
          class="u-margin-bottom-4x"
          multi-line
          :title="$t('pages.login.alertLoginTitle')"
        >
          <p>{{ $t('pages.login.alertLoginFailed') }}</p>
          <template #actions>
            <BaseButton
              to="/request-password"
              class="u-margin-top-2x"
              variant="tertiary"
              size="sm"
            >
              {{ $t('pages.login.buttonForgotPassword') }}
            </BaseButton>
          </template>
        </BaseAlert>

        <BaseAlert
          v-model="showAttempts"
          variant="warning"
          class="u-margin-bottom-4x"
          data-cy="alert-login-failed-attempts"
          multi-line
          :title="
            $t('pages.login.alertLoginLockedTitle', { attempts: attempts })
          "
        >
          <i18next path="pages.login.alertLoginLocked" tag="p">
            <strong>{{ attempts }}</strong>
          </i18next>
        </BaseAlert>

        <BaseAlert
          v-model="showLockedDuration"
          variant="warning"
          class="u-margin-bottom-4x"
          data-cy="alert-login-failed-attempts-duration"
          multi-line
          :title="
            $t('pages.login.alertLoginLockedTitle', { attempts: attempts })
          "
        >
          <i18next path="pages.login.alertLoginLockedTime" tag="p">
            <strong>{{ attempts }}</strong>
            {{
              $t('pages.login.alertLockedTime', {
                duration: time.duration,
                unit: time.unit
              })
            }}
          </i18next>
        </BaseAlert>

        <div class="row">
          <div class="col-sm-6 u-margin-bottom-4x">
            <BaseButton
              :loading="isLoading"
              type="submit"
              full-width
              data-cy="login-submit"
            >
              {{ $t('pages.login.buttonLogin') }}
            </BaseButton>
          </div>
          <div class="col-sm-6 u-margin-bottom-4x first-sm">
            <BaseButton to="/request-password" variant="secondary" full-width>
              {{ $t('pages.login.buttonForgotPassword') }}
            </BaseButton>
          </div>
        </div>
      </form>
    </section>

    <section class="account__column account__column--showcase">
      <img alt="" src="/img/login.svg" />
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { BaseAlert, BaseButton } from '@te/cdev-design-system';
import AuthService from '@/common/services/Auth';
import { whitelabel } from '@te/cdev-utils';
import { Period, minutesToText } from '@/common/helpers/format';
import { PromiseResponse } from '@/common/global.interfaces';

enum LOGIN {
  minNumberOfAttempts = 3
}

enum LOGIN_CODES {
  LOGIN_FAILED = 'LOGIN_FAILED',
  LOCKED_STATE_ACCOUNT = 'LOCKED_STATE_ACCOUNT',
  DISABLED_ACCOUNT = 'DISABLED_ACCOUNT',
  PASSWORD_EXPIRED_ACCOUNT = 'PASSWORD_EXPIRED_ACCOUNT',
  OVER_LOCKOUT_ATTEMPTS = 'OVER_LOCKOUT_ATTEMPTS',
  ACCOUNT_MIGRATED = 'ERROR_USE_THOUSANDEYES_LOGIN'
}

interface LoginFailedData {
  REMAINING_LOGIN_ATTEMPTS: number | null;
  LOCKOUT_DURATION_SECONDS: number | null;
}

type LoginFailedResponse = PromiseResponse<
  LoginFailedData | undefined,
  LOGIN_CODES.LOGIN_FAILED
>;

interface LockedAccountData {
  LOCKED_UNTIL: string | null;
  LOCKOUT_DURATION_SECONDS: number | null;
}

type LockedAccountResponse = PromiseResponse<
  LockedAccountData,
  LOGIN_CODES.LOCKED_STATE_ACCOUNT
>;

type OverLockoutAttemptsResponse = PromiseResponse<
  LockedAccountData,
  LOGIN_CODES.OVER_LOCKOUT_ATTEMPTS
>;

type DisabledAccountResponse = PromiseResponse<
  undefined,
  LOGIN_CODES.DISABLED_ACCOUNT
>;

type AccountMigratedResponse = PromiseResponse<
  undefined,
  LOGIN_CODES.ACCOUNT_MIGRATED
>;

interface PasswordExpiredData {
  USER_ID: string;
  RESET_TOKEN: string;
  PASS_EXPIRY_PERIOD_DAYS: number | undefined;
}

type PasswordExpiredResponse = PromiseResponse<
  PasswordExpiredData,
  LOGIN_CODES.PASSWORD_EXPIRED_ACCOUNT
>;

type AuthFailedResponses =
  | LoginFailedResponse
  | LockedAccountResponse
  | PasswordExpiredResponse
  | DisabledAccountResponse
  | OverLockoutAttemptsResponse
  | AccountMigratedResponse;

export default defineComponent({
  metaInfo() {
    return {
      title: this.$t('pages.login.title')
    };
  },
  components: {
    BaseAlert,
    BaseButton
  },
  data() {
    return {
      isLoading: false,
      email: '',
      password: '',
      showLoginFailedAlert: false,
      isLocked: false,
      showDisabledAccountAlert: false,
      showAccountMigratedAlert: false,
      permanentLockout: false,
      duration: 0,
      attempts: 0,
      hideImage: whitelabel('hideLoginImage')
    };
  },
  computed: {
    minAttempts(): boolean {
      return !!(this.attempts && this.attempts <= LOGIN.minNumberOfAttempts);
    },
    lockedDuration(): number {
      return Math.round(this.duration / 60);
    },
    showAttempts(): boolean {
      return this.minAttempts && this.lockedDuration === 0;
    },
    showLockedDuration(): boolean {
      return this.minAttempts && this.lockedDuration > 0;
    },
    time(): Period {
      return minutesToText(this.lockedDuration);
    }
  },
  mounted() {
    if (this.$route.query.accountMigrated === 'true') {
      this.showAccountMigratedAlert = true;
    }
  },
  methods: {
    handlePasswordExpired(data: PasswordExpiredData) {
      const resetToken = data.RESET_TOKEN;
      const userId = data.USER_ID;
      const passwordExpiryTime = data.PASS_EXPIRY_PERIOD_DAYS;

      this.$router.push({
        name: 'PasswordExpired',
        params: {
          userId: userId,
          token: resetToken,
          passwordExpiryTime: `${passwordExpiryTime}`
        }
      });
    },
    handleLockedAccount(data: LockedAccountData) {
      this.showLoginFailedAlert = true;

      const { LOCKED_UNTIL, LOCKOUT_DURATION_SECONDS } = data;

      this.isLocked = typeof LOCKED_UNTIL !== 'undefined';
      this.permanentLockout = LOCKED_UNTIL === null;
      this.handleShowLockoutDuration(LOCKOUT_DURATION_SECONDS);
    },
    handleShowLockoutDuration(duration = 0) {
      this.duration = duration;
    },
    async authenticate(email: string, password: string) {
      this.isLoading = true;

      try {
        await AuthService.authenticate(email, password);
        AuthService.goToDefaultPath(this.$route);
      } catch (error) {
        this.isLoading = false;
        console.error(error);

        if (error instanceof Response) {
          const responseBody: AuthFailedResponses = await error.json();
          const { data, code } = responseBody;

          switch (code) {
            case LOGIN_CODES.DISABLED_ACCOUNT:
              this.showDisabledAccountAlert = true;
              break;
            case LOGIN_CODES.PASSWORD_EXPIRED_ACCOUNT:
              this.handlePasswordExpired(data);
              break;
            case LOGIN_CODES.LOCKED_STATE_ACCOUNT:
              this.handleLockedAccount(data);
              break;
            case LOGIN_CODES.OVER_LOCKOUT_ATTEMPTS:
              this.handleLockedAccount(data);
              break;
            case LOGIN_CODES.LOGIN_FAILED:
              this.showLoginFailedAlert = true;
              const { REMAINING_LOGIN_ATTEMPTS, LOCKOUT_DURATION_SECONDS } =
                data ?? {};
              this.attempts = REMAINING_LOGIN_ATTEMPTS;
              this.handleShowLockoutDuration(LOCKOUT_DURATION_SECONDS);
              break;
            case LOGIN_CODES.ACCOUNT_MIGRATED:
              this.showAccountMigratedAlert = true;
              break;
            default:
              this.showLoginFailedAlert = true;
          }
        } else {
          // Always show the login failed alert for other errors
          this.showLoginFailedAlert = true;
        }
      }
    }
  }
});
</script>
